import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios, { AxiosError } from "axios"
import { ReactNode } from "react"
import { getBaseEndpoint } from "../endpoints/endpointManager"
import { practiceUpdated } from "../../domain/Practice/model"
import { Practice, PracticeStatus } from "../../domain/Practice/schema"
import {
  PracticeName,
  practiceLoaderSlice,
} from "../../domain/Practice/loaders/loader"
import { RootState } from "../../reduxStore/store"
import { DataStatus } from "../../domain/enums"
import { apiService } from "../shared/apiService"
import { selectAccountGuid } from "../authentication/authenticationSlice"

export type PracticeProfileStepName =
  | "authAndReleases"
  | "practiceInformation"
  | "tax"
  | "liability"
  | "offices"
  | "practiceAgreement"
export type StepStatus =
  | "complete"
  | "notStarted"
  | "notRequired"
  | "pending"
  | "ready"
export const editablePracticeStates: PracticeStatus[] = ["pending", "active"] // for user that has edit auth, AR can also edit practice with pending activation status

export interface PracticeProfileStep {
  name: PracticeProfileStepName
  status: StepStatus
  disclosureChildren?: ReactNode
  label?: string
}

export interface PracticeProfileStepStatusUpdate {
  name: PracticeProfileStepName
  status: StepStatus
}

export interface PracticeInformationType {
  id: string
  name: string
  status: PracticeStatus
  isVisible: boolean
  websiteUrl: string
  primaryEmailAddress: string
  overview: string
  logoImageUrl: string
  tags: string[]
  authReleaseRequired: boolean | null
  hasOnlineServicesInCalifornia: boolean | null
}

export interface PracticeInfoStatus {
  addressCompleted: boolean
  contactCompleted: boolean
}

export interface PracticeProfileProcessState {
  practiceInformationDataStatus: DataStatus
  allPracticeNamesDataStatus: DataStatus
  name: string
  practiceAgreementReady: boolean
  selectedStep: PracticeProfileStepName
  allPracticeNames: PracticeName[]
  status: StepStatus
  pageStatus: DataStatus
  tagList: string[]
  allTagsDataStatus: DataStatus
}

export interface GetPracticeDataArg {
  practiceId: string
  userId: string
  isAffiliateRelations: boolean
}

export interface PracticeProfileTextReturnType {
  name: string
  practiceProfileSteps: {
    authAndReleases: string
    practiceInformation: string
    tax: string
    liability: string
    offices: string
    practiceAgreement: string
  }
}

// this will eventually get too big...
// we will need to split into the process slice which keeps track of the practice status
// and additional slices for each step of the practice setup
const initialState: PracticeProfileProcessState = {
  practiceInformationDataStatus: DataStatus.LOADING,
  allPracticeNamesDataStatus: DataStatus.LOADING,
  allTagsDataStatus: DataStatus.LOADING,
  name: "",
  practiceAgreementReady: false,
  selectedStep: "authAndReleases",
  allPracticeNames: [],
  status: "notStarted",
  pageStatus: DataStatus.LOADING,
  tagList: [],
}

const getTagsEndpoint = (): string => {
  return getBaseEndpoint() + `/practice/tags`
}

// TODO: absorb this into the practiceHeader loader
export const getPracticeNamesData = createAsyncThunk<PracticeName[]>(
  "practiceHeader/getPracticeNames",
  async (_args, { dispatch, getState }) => {
    const state = getState() as RootState
    const userId = selectAccountGuid(state)
    dispatch(
      practiceProfileProcessSlice.actions.allPracticeNamesDataStatusUpdated(
        DataStatus.SENDING,
      ),
    )

    try {
      const data = !!userId
        ? await apiService.practice.get.PRACTICE_NAMES_BY_USER_ID({
            userId,
          })
        : []
      const practiceNames: PracticeName[] = data.map(
        ({ id, name }: PracticeName) => ({
          id,
          name,
        }),
      )

      dispatch(
        practiceProfileProcessSlice.actions.allPracticeNamesUpdated(
          practiceNames,
        ),
      )
      dispatch(
        practiceProfileProcessSlice.actions.allPracticeNamesDataStatusUpdated(
          DataStatus.LOADED,
        ),
      )

      return practiceNames
    } catch (error) {
      if (error && (error as AxiosError).response?.status === 404) {
        const payload = initialState.allPracticeNames
        dispatch(
          practiceProfileProcessSlice.actions.allPracticeNamesUpdated(payload),
        )
        dispatch(
          practiceProfileProcessSlice.actions.allPracticeNamesDataStatusUpdated(
            DataStatus.ERROR,
          ),
        )
        return payload
      } else {
        console.error("getPracticeNames: " + error)
        throw error
      }
    }

    // In case of unexpected scenarios, return an empty array
    return [] as PracticeName[]
  },
)

// TODO: absorb this into the practiceHeader loader
export const getAllTagsThunk = createAsyncThunk(
  "practiceHeader/GetAllTags",
  async (_args, { dispatch }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        practiceTagsDataStatus: DataStatus.SENDING,
      }),
    )
    try {
      const response = await axios.get(getTagsEndpoint())
      if (response.status === 200) {
        const list: string[] = []
        response.data.forEach((tag: string) => {
          list.push(tag)
        })
        dispatch(practiceProfileProcessSlice.actions.allTagsListUpdated(list))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            practiceTagsDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error("Failed to fetch tags")
      }
    } catch (error) {
      console.error("getAllTags: " + error)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceTagsDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

const practiceProfileProcessSlice = createSlice({
  name: "practiceProfileProcess",
  initialState,
  reducers: {
    // practice names will be eliminated by the quicksearch for practice
    allPracticeNamesUpdated: (
      state: { allPracticeNames: PracticeName[] },
      { payload }: PayloadAction<PracticeName[]>,
    ) => {
      state.allPracticeNames = payload
    },
    // practice names will be eliminated by the quicksearch for practice
    allPracticeNamesDataStatusUpdated: (
      state,
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.allPracticeNamesDataStatus = payload
    },
    // this can be absorbed into a useEffect that calculates selected step by url
    selectedStepUpdated: (
      state,
      action: PayloadAction<PracticeProfileStepName>,
    ) => {
      state.selectedStep = action.payload
    },
    // tags can be absorbed into main practice model (similar to TaxInfoTables)
    allTagsDataStatus: (state, action: PayloadAction<DataStatus>) => {
      state.allTagsDataStatus = action.payload
    },
    // this should be in the practice header loader
    allTagsListUpdated: (state, action: PayloadAction<string[]>) => {
      state.tagList = action.payload
    },
  },
  // practice names will be eliminated by the quicksearch for practice
  extraReducers: (builder) => {
    builder.addCase(
      practiceUpdated,
      (state, action: PayloadAction<Partial<Practice>>) => {
        // updates practice names for practice dropdown selection if practice name modified
        if (action.payload?.name && action.payload.id) {
          const practiceName = action.payload.name
          // update the modified practice name in the list of practiceNames
          const practiceNames = state.allPracticeNames.map(
            (practiceNameObj: PracticeName) =>
              practiceNameObj.id === action.payload.id
                ? { ...practiceNameObj, name: practiceName }
                : practiceNameObj,
          )
          state.allPracticeNames = practiceNames
        }
      },
    )
  },
})

export const {
  allPracticeNamesUpdated,
  selectedStepUpdated,
  allTagsListUpdated,
  allTagsDataStatus,
} = practiceProfileProcessSlice.actions

// TODO: this will be removed with the search service api
export const selectAllPracticeNames = (state: {
  practiceProfileProcess: PracticeProfileProcessState
}): PracticeName[] => {
  return state.practiceProfileProcess.allPracticeNames
}

// TODO: this will be removed with the search service api
export const selectAllPracticeNamesDataStatus = (
  state: RootState,
): DataStatus => {
  return state.practiceProfileProcess.allPracticeNamesDataStatus
}

// TODO: move this to practice model
export const selectAllTags = (state: RootState): string[] => {
  return state.practiceProfileProcess.tagList
}

// TODO: move this to practiceHeader loader
export const selectAllTagsDataStatus = (state: RootState): DataStatus => {
  return state.practiceProfileProcess.allTagsDataStatus
}

// TODO: remove this and drive the selected step from the url (similar to provider)
export const selectCurrentSelectedStep = (
  state: RootState,
): PracticeProfileStepName => {
  return state.practiceProfileProcess.selectedStep
}

export default practiceProfileProcessSlice.reducer
